import { bundledMessages } from '@/i18n/index.js'
import { STORYBLOK_ACCESS_TOKEN, isStoryblokEnabled } from '@/constants/storyblok.js'

const CDN_BASE = 'https://api.storyblok.com/v2/cdn'
const DATASOURCE_SLUG = 'game-on-labels'
const PER_PAGE = 1000

const LOCALE_TO_DIMENSION = {
    nl: 'nl',
    de: 'de-de',
    cs: 'cs',
    da: 'da',
    es: 'es',
    fr: 'fr-fr',
    it: 'it',
    pl: 'pl',
}

class StoryblokService {
    constructor() {
        this.accessToken = STORYBLOK_ACCESS_TOKEN
        this.version = import.meta.env.PROD ? 'published' : 'draft'
        // slug+language -> in-flight or resolved fetch, so repeated mounts of
        // the same story reuse one request for the lifetime of the page.
        this.storyCache = new Map()
    }

    isConfigured() {
        return isStoryblokEnabled
    }

    async fetchAllPages(slug, dimension) {
        const entries = []
        let page = 1

        while (true) {
            const params = new URLSearchParams({
                token: this.accessToken,
                version: this.version,
                datasource: slug,
                per_page: PER_PAGE,
                page,
            })
            if (dimension) {
                params.set('dimension', dimension)
            }

            const response = await fetch(`${CDN_BASE}/datasource_entries?${params}`)
            if (!response.ok) {
                throw new Error(`Storyblok fetch failed for "${slug}" (page ${page}): ${response.status}`)
            }

            const data = await response.json()
            const batch = data.datasource_entries ?? []
            entries.push(...batch)

            if (batch.length < PER_PAGE) break
            page++
        }

        return entries
    }

    resolveValue(entry, dimension) {
        const raw = (dimension && entry.dimension_value) || entry.value
        if (!raw) return undefined

        if (raw.trimStart().startsWith('[')) {
            try {
                return JSON.parse(raw)
            } catch {
                // Not a valid JSON array — treat as plain string
            }
        }

        return raw
    }

    flatToNested(entries, dimension, base = bundledMessages.en) {
        // Deep-clone the bundled base for this locale so every key has an
        // offline fallback before applying Storyblok values on top.
        const result = JSON.parse(JSON.stringify(base))

        for (const entry of entries) {
            const value = this.resolveValue(entry, dimension)
            if (value === undefined) continue

            const keys = entry.name.split('.')
            let node = result
            for (let i = 0; i < keys.length - 1; i++) {
                if (!node[keys[i]] || typeof node[keys[i]] !== 'object' || Array.isArray(node[keys[i]])) {
                    node[keys[i]] = {}
                }
                node = node[keys[i]]
            }
            node[keys[keys.length - 1]] = value
        }

        return result
    }

    async fetchAllTranslations(locale) {
        if (!this.isConfigured()) return null

        const dimension = LOCALE_TO_DIMENSION[locale] ?? null
        const entries = await this.fetchAllPages(DATASOURCE_SLUG, dimension)

        // Fall back to the bundled file for this locale if one exists,
        // otherwise to English. Any keys missing from the base are still
        // resolved by vue-i18n's `fallbackLocale: 'en'`.
        const base = bundledMessages[locale] ?? bundledMessages.en

        return this.flatToNested(entries, dimension, base)
    }

    /**
     * Fetch a single story (a page of content blocks) from the Content
     * Delivery API. Returns null when Storyblok is unconfigured or the slug
     * does not exist, so callers can render nothing instead of failing.
     */
    async fetchStory(slug, { locale = null, cache = true } = {}) {
        if (!this.isConfigured()) return null

        const language = (locale && LOCALE_TO_DIMENSION[locale]) || null
        const cacheKey = `${slug}:${language ?? 'default'}`

        if (cache && this.storyCache.has(cacheKey)) {
            return this.storyCache.get(cacheKey)
        }

        const params = new URLSearchParams({
            token: this.accessToken,
            version: this.version,
        })
        if (language) {
            params.set('language', language)
        }

        const request = fetch(`${CDN_BASE}/stories/${slug}?${params}`)
            .then(async (response) => {
                if (response.status === 404) return null
                if (!response.ok) {
                    throw new Error(
                        `Storyblok fetch failed for story "${slug}": ${response.status}`,
                    )
                }
                const data = await response.json()
                return data.story ?? null
            })
            .catch((error) => {
                // Don't cache a failure — the next mount should retry.
                this.storyCache.delete(cacheKey)
                throw error
            })

        if (cache) {
            this.storyCache.set(cacheKey, request)
        }

        return request
    }
}

/**
 * Build a URL through Storyblok's image service. `width`/`height` of 0 means
 * "keep the aspect ratio on that axis". SVGs and non-Storyblok assets are
 * returned untouched — the service does not process them.
 */
export function storyblokImage(asset, { width = 0, height = 0, quality = 80 } = {}) {
    const url = typeof asset === 'string' ? asset : asset?.filename
    if (!url) return ''
    if (!url.includes('a.storyblok.com') || url.endsWith('.svg')) return url

    return `${url}/m/${width}x${height}/filters:quality(${quality})`
}

export const storyblokService = new StoryblokService()
